import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";
import type { RenewalTimelinePoint } from "@/types";

interface RenewalTimelineChartProps {
  data: RenewalTimelinePoint[];
}

/**
 * Bar chart showing how many policies renew (effective start) each month.
 * Helps visualize renewal concentration and seasonal patterns.
 */
export const RenewalTimelineChart = ({ data }: RenewalTimelineChartProps) => {
  const { theme } = useAppTheme();

  const chartData = useMemo(() => {
    if (data.length === 0) return [];

    // Format "2025-01" → "Jan 25"
    return data.map((d) => {
      const [year, month] = d.month.split("-");
      const date = new Date(Number(year), Number(month) - 1);
      const label = date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      return { name: label, count: d.policy_count };
    });
  }, [data]);

  if (chartData.length === 0) return <EmptyChartState />;

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={chartData} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} />
        <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10 }} allowDecimals={false} />
        <Tooltip contentStyle={getChartTooltipStyle(theme)} />
        <Bar dataKey="count" name="Policies" fill={theme.palette.primary.main} />
      </BarChart>
    </ResponsiveContainer>
  );
};
